import React, { useRef } from 'react';
import { PaperclipIcon, FileTextIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Attachment } from '../../types/composer';

/** Human-readable file size (kB / MB). */
function formatSize(bytes: number, locale: string): string {
  if (bytes < 1024) return `${bytes} o`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toLocaleString(locale, {
    maximumFractionDigits: 0
  })} ko`;
  return `${(kb / 1024).toLocaleString(locale, {
    maximumFractionDigits: 1
  })} Mo`;
}
interface AttachmentPickerProps {
  attachments: Attachment[];
  onChange: (attachments: Attachment[]) => void;
}
export function AttachmentPicker({
  attachments,
  onChange
}: AttachmentPickerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const inputRef = useRef<HTMLInputElement>(null);
  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: Attachment[] = Array.from(files).map((file, i) => ({
      id: `${Date.now()}-${i}-${file.name}`,
      name: file.name,
      size: file.size
    }));
    onChange([...attachments, ...next]);
  };
  const remove = (id: string) => onChange(attachments.filter(a => a.id !== id));
  return <div data-id="element-3751" data-mp="nm7d0pjgv2rm">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap" data-id="element-3752" data-mp="nlxn11yf8vlx">
        <label className="block text-sm font-medium text-slate-700" data-id="element-3753" data-mp="no0hby6lxawg">
          {t('Pièces jointes', 'Attachments')}
        </label>
        <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#4F46E5] bg-white border border-[#4F46E5] rounded-md hover:bg-indigo-50 transition-colors" data-id="element-3754" data-mp="wm2sv7nyvlxz">
          <PaperclipIcon className="w-3.5 h-3.5" data-id="element-3755" data-mp="33dv78d73ih6q" />
          {t('Ajouter une pièce jointe', 'Add an attachment')}
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" aria-hidden="true" tabIndex={-1} onChange={e => {
        addFiles(e.target.files);
        e.target.value = '';
      }} data-id="element-3756" data-mp="38d7tavq57k89" />
      </div>

      {attachments.length === 0 ? <p className="text-xs text-slate-400" data-id="element-3757" data-mp="3a7trdegfyhov">
          {t('Aucune pièce jointe.', 'No attachment.')}
        </p> : <ul className="space-y-2" data-id="element-3758" data-mp="3jabfjyfnpkei">
          {attachments.map(file => <li key={file.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2" data-id="element-3759" data-mp="3j88d2exbhysw">
              <div className="w-8 h-8 rounded bg-indigo-50 text-[#4F46E5] flex items-center justify-center flex-shrink-0" data-id="element-3760" data-mp="3sz337udopijm">
                <FileTextIcon className="w-4 h-4" data-id="element-3761" data-mp="3o3eaob0311wt" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-3762" data-mp="5whsas4zz7ff">
                <p className="text-sm font-medium text-slate-800 truncate" data-id="element-3763" data-mp="1u3ambd2edpq">
                  {file.name}
                </p>
                <p className="text-xs text-slate-400" data-id="element-3764" data-mp="khvk4j2onspf">
                  {formatSize(file.size, locale)}
                </p>
              </div>
              <button type="button" onClick={() => remove(file.id)} aria-label={t('Retirer la pièce jointe', 'Remove attachment')} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0" data-id="element-3765" data-mp="pgp0cj9ctxta">
                <XIcon className="w-4 h-4" data-id="element-3766" data-mp="1q2riprj09i8" />
              </button>
            </li>)}
        </ul>}
    </div>;
}